import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileDown, ExternalLink, Lock, ShieldCheck, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, downloadBlob, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COUNTRIES } from "@/lib/hubs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Header } from "@/pages/dashboard/Insights";

const LABELS = { rising_monthly: "Rising Star · monthly", rising_yearly: "Rising Star · annual", elite_monthly: "Elite Label · monthly", elite_yearly: "Elite Label · annual", mastering_credit: "SmartMastering credit" };

export const Billing = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/payments/invoices").then((r) => setD(r.data)); }, []);
  const portal = async () => {
    try { const { data } = await api.post("/payments/portal", { origin_url: window.location.origin }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="billing-tab">
      <Header eyebrow="Account" title="Billing & invoices" />
      <div className="surface p-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1"><div className="eyebrow">Current plan</div><div data-testid="billing-current-plan" className="font-display text-3xl font-black capitalize mt-1">{user.plan === "free" ? "Starter" : user.plan === "rising" ? "Rising Star" : "Elite Label"} {user.billing_cycle && <span className="text-base font-mono text-zinc-500">· {user.billing_cycle}</span>}</div></div>
        <button data-testid="billing-upgrade-button" onClick={() => nav("/pricing")} className="h-11 px-5 rounded-full bg-[var(--hub)] text-white font-semibold">Change plan</button>
        <button data-testid="billing-portal-button" onClick={portal} className="h-11 px-5 rounded-full border border-white/15 inline-flex items-center gap-2"><ExternalLink size={14} /> Customer portal</button>
      </div>
      <div className="surface mt-4" data-testid="invoice-list">
        <div className="p-5 eyebrow">Invoices</div>
        {d?.invoices.length === 0 && <div className="px-5 pb-6 text-zinc-500 text-sm">No invoices yet.</div>}
        {d?.invoices.map((i) => (
          <div key={i.session_id} className="px-5 py-3 border-t border-white/5 flex items-center gap-4 text-sm">
            <span className="flex-1">{LABELS[i.lookup_key] || "Artist tip"}</span><span className="font-mono text-zinc-500">{i.created_at.slice(0, 10)}</span><span className="font-mono">${i.amount.toFixed(2)}</span>
            <button data-testid={`invoice-pdf-${i.session_id}`} onClick={() => downloadBlob(`/payments/invoices/${i.session_id}/pdf`, `soundra-invoice-${i.session_id.slice(-8)}.pdf`)} className="text-zinc-400 hover:text-white"><FileDown size={16} /></button>
          </div>
        ))}
      </div>
      {d?.tips_received?.length > 0 && (
        <div className="surface mt-4"><div className="p-5 eyebrow">Tips received</div>
          {d.tips_received.map((t) => <div key={t.tip_id} className="px-5 py-3 border-t border-white/5 flex justify-between text-sm"><span>${t.amount.toFixed(2)}</span><span className="font-mono text-xs text-zinc-500">{t.payout_status.replaceAll("_", " ")}</span></div>)}
        </div>
      )}
      {d && <p className="text-xs text-zinc-600 mt-4">{d.merchant.entity} · {d.merchant.address} · VAT {d.merchant.vat} · {d.merchant.support}</p>}
    </div>
  );
};

export const Payouts = () => {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/connect/status").then((r) => setS(r.data)); }, []);
  const onboard = async () => {
    setBusy(true);
    try { const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!s) return null;
  return (
    <div data-testid="payouts-tab">
      <Header eyebrow="Stripe Connect" title="Payouts" />
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="surface p-6"><div className="eyebrow">Held · pending KYC</div><div data-testid="payouts-held" className="font-display text-4xl font-black mt-2">${s.held.toFixed(2)}</div></div>
        <div className="surface p-6"><div className="eyebrow">Ready for payout</div><div data-testid="payouts-ready" className="font-display text-4xl font-black mt-2">${s.ready.toFixed(2)}</div></div>
      </div>
      <div className="surface p-6 mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
        {s.kyc_complete ? <ShieldCheck className="text-emerald-400" size={28} /> : <Lock className="text-amber-300" size={28} />}
        <div className="flex-1">
          <div className="font-display text-xl font-bold" data-testid="payouts-kyc-status">{s.kyc_complete ? "Identity & bank verified" : "Payouts locked until verification"}</div>
          <p className="text-sm text-zinc-400">Tips and digital sales are routed to your bank once Stripe completes identity (KYC) and bank account verification.</p>
        </div>
        {!s.kyc_complete && <button data-testid="payouts-onboard-button" disabled={busy} onClick={onboard} className="h-11 px-5 rounded-full bg-white text-black font-semibold">{s.connected ? "Continue verification" : "Start verification"}</button>}
      </div>
    </div>
  );
};

const ImgUpload = ({ label, value, onUploaded, kind, round }) => {
  const up = async (file) => {
    const fd = new FormData(); fd.append("file", file); fd.append("kind", kind);
    try { const { data } = await api.post("/uploads/image", fd); onUploaded(data.url); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <label data-testid={`profile-${kind}-upload`} className={`relative block overflow-hidden border border-dashed border-white/15 cursor-pointer hover:border-white/40 bg-white/[0.03] ${round ? "w-28 h-28 rounded-full" : "h-28 rounded-2xl flex-1"}`}>
      <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => e.target.files[0] && up(e.target.files[0])} />
      {value ? <img src={mediaUrl(value)} alt="" className="w-full h-full object-cover" /> : <div className="h-full flex items-center justify-center gap-2 text-xs text-zinc-500"><ImagePlus size={16} />{label}</div>}
    </label>
  );
};

export const ProfileEditor = () => {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user.name, bio: user.bio || "", country: user.country || "US", avatar: user.avatar, cover: user.cover, socials: { tiktok: "", instagram: "", youtube: "", x: "", spotify: "", website: "", ...(user.socials || {}) } });
  const save = async () => {
    try { const { data } = await api.patch("/auth/me", f); setUser(data); toast.success("Profile saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="profile-editor" className="max-w-3xl">
      <Header eyebrow="Public vitrine" title="Profile" />
      <div className="flex gap-4 items-center">
        <ImgUpload label="Avatar" kind="avatar" round value={f.avatar} onUploaded={(u) => setF({ ...f, avatar: u })} />
        <ImgUpload label="Cover photo" kind="cover" value={f.cover} onUploaded={(u) => setF({ ...f, cover: u })} />
      </div>
      <div className="space-y-3 mt-6">
        <Input data-testid="profile-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Name" className="h-12 bg-white/5 border-white/10" />
        <Textarea data-testid="profile-bio-input" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} placeholder="Biography" rows={4} maxLength={600} className="bg-white/5 border-white/10" />
        <select data-testid="profile-country-select" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="h-12 w-full rounded-md px-3 bg-white/5 border border-white/10 text-sm">
          {Object.entries(COUNTRIES).filter(([k]) => k !== "GLOBAL").map(([k, v]) => <option key={k} value={k} className="bg-zinc-900">{v}</option>)}
        </select>
        <div className="grid sm:grid-cols-2 gap-3 pt-2">
          {Object.keys(f.socials).map((k) => <Input key={k} data-testid={`profile-social-${k}`} value={f.socials[k]} onChange={(e) => setF({ ...f, socials: { ...f.socials, [k]: e.target.value } })} placeholder={`${k === "x" ? "X" : k[0].toUpperCase() + k.slice(1)} URL`} className="h-11 bg-white/5 border-white/10" />)}
        </div>
      </div>
      <button data-testid="profile-save-button" onClick={save} className="mt-6 h-11 px-6 rounded-full bg-[var(--hub)] text-white font-semibold">Save profile</button>
    </div>
  );
};
